import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LeafIcon } from 'lucide-react';
import { Faq } from '../shared/Faq';
import { GiftCardBlock } from '../shared/GiftCardBlock';
import { HeadingText } from '../shared/HeadingText';
import { RoutineCard } from '../shared/RoutineCard';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { TrustBadges } from '../shared/TrustBadges';
import { Drawer } from '../shared/Drawer';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { shopFaq } from '../../data/faq';
import { usePages } from '../../contexts/SiteContext';
import { founder } from '../../data/content';
import { listNl, plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { quietLink, sectionHeading } from '../../ui/sections';
import { FilterPanel } from './ShopFilters';
import { ShopCollection } from './ShopCollection';
import { IngredientIndex } from './IngredientIndex';
import { ROUTINES_SHOWN } from './shopRules';
import { useShopFilters } from './useShopFilters';

/**
 * De webshop: compacte kop, de collectie, en daarna het verhaal (docs/VORMGEVING.md §3):
 * kop + collectie (sand) · beloftes (shell) · protocollen (sand) · wat er écht in zit (shell) · abonnement (klei) ·
 * merken (shell) · vragen (sand) · cadeaubon (blush).
 */
export function Shop() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    products,
    brands,
    routines
  } = useCatalog();
  const f = useShopFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const copy = pages.shop;
  const vars = {
    count: plural(products.length, 'product', 'producten'),
    brands: listNl(brands.map(b => b.name))
  };
  return <>
      {/* Compacte kop: wie hier komt, wil producten zien. Het verhaal staat verderop. */}
      <section className="bg-sand" data-id="element-6820" data-mp="3g1jl8t2wk3ev">
        <div className="mx-auto flex max-w-content flex-col gap-5 px-6 pb-2 pt-10 lg:flex-row lg:items-end lg:justify-between lg:gap-12 lg:px-10 lg:pt-16" data-id="element-6821" data-mp="3pvyrp60qt0er">
          <div className="max-w-3xl" data-id="element-6822" data-mp="3luvd0muflaf4">
            <h1 className="font-display text-[2.5rem] font-light leading-[1.02] tracking-[-0.015em] text-ink sm:text-[3.4rem] lg:text-[4rem]" data-id="element-6823" data-mp="3gvadobbcn01v">
              <HeadingText heading={copy.hero.heading} data-id="element-6824" data-mp="3bv4mew8zrfkb" />
            </h1>
            {copy.hero.body && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-6825" data-mp="36y4o57idvh3r">{fill(copy.hero.body, vars)}</p>}
          </div>
          {/* Twijfel? Persoonlijk, met haar gezicht: het huidadvies voelt als een vraag aan Sarah, niet als een formulier. */}
          {copy.hero.secondaryCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className="group inline-flex min-h-[52px] shrink-0 items-center gap-3 self-start rounded-full border border-ink/15 bg-shell py-1.5 pl-1.5 pr-5 text-left text-[14px] text-ink transition-[border-color,transform] duration-150 ease-soft hover:border-ink/40 active:scale-[0.98] lg:self-end" data-id="element-6826" data-mp="2t6r7c1bv0xcf">
              <img src={founder.portrait} alt="" width={40} height={40} className="h-10 w-10 shrink-0 rounded-full object-cover object-top" data-id="element-6827" data-mp="2e0tuhnj0odxb" />
              <span className="leading-snug" data-id="element-6828" data-mp="2o0krbvv1v50k">{fill(copy.hero.secondaryCta)}</span>
              <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6829" data-mp="1qd3vafmef2qj" />
            </button>}
        </div>
      </section>

      <ShopCollection f={f} onOpenFilters={() => setFiltersOpen(true)} data-id="element-6830" data-mp="20b03ayk1hhkf" />

      {/* Zekerheden ná de collectie: ze bevestigen de keuze, maar staan het kiezen niet in de weg. */}
      <TrustBadges data-id="element-6831" data-mp="17mui6ap2pqk2" />

      {/* Protocollen direct na de collectie: wie twijfelt tussen losse producten, krijgt hier het complete plan. */}
      {routines.length > 0 && <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="shop-routines" data-id="element-6832" data-mp="1cjkwotl8ce3d">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6833" data-mp="3e8s7pnjyqw1t">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-6834" data-mp="39ctffoviktw4">
              <div className="max-w-2xl" data-id="element-6835" data-mp="3o16cyqa92ksq">
                <h2 id="shop-routines" className={`${sectionHeading} text-ink`} data-id="element-6836" data-mp="3o1z9kyfhvtx5">
                  <HeadingText heading={copy.routines.heading} data-id="element-6837" data-mp="3j9660dvrirmn" />
                </h2>
                {copy.routines.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-6838" data-mp="udngzb68irk">{fill(copy.routines.body)}</p>}
              </div>
              {copy.routines.cta && <Link to="/routines" className={`${quietLink} text-ink`} data-id="element-6839" data-mp="aunzwhpdtm5o">
                  {fill(copy.routines.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6840" data-mp="5w2zah67x0gp" />
                </Link>}
            </div>

            <ul className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-6841" data-mp="2cnc71255ctqq">
              {routines.slice(0, ROUTINES_SHOWN).map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-6842" data-mp="n3u92qj3d48v">
                  <RoutineCard routine={r} data-id="element-6843" data-mp="n6q8kjal80zf" />
                </li>)}
            </ul>
          </div>
        </section>}

      {/* Wat er écht in zit: voor wie wil weten wáárom iets werkt. */}
      <IngredientIndex data-id="element-6844" data-mp="idniil50r9nz" />

      <SubscriptionBlock data-id="element-6845" data-mp="icvnpcj8xfyr" />

      {/* Merken */}
      <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="shop-brands" data-id="element-6846" data-mp="ibkjjblw2674">
        <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-6847" data-mp="4ckzyid8ghht">
          <div className="lg:col-span-5" data-id="element-6848" data-mp="9aw6ok88ebvt">
            <h2 id="shop-brands" className={`${sectionHeading} text-ink`} data-id="element-6849" data-mp="3qlm46dsiy4ba">
              {brands.length === 1 ? 'Waarom juist dit merk' : `Waarom juist deze ${brands.length}`}
            </h2>
            <p className="mt-6 flex items-start gap-3 text-[17px] leading-relaxed text-muted" data-id="element-6850" data-mp="1otzdyeggidfv">
              <LeafIcon className="mt-1.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6851" data-mp="1tskb250ewh03" />
              {/* De boomclaim alleen zolang emerginC echt in het assortiment zit. */}
              Duurzaam is een product dat zin heeft en doet waarvoor het gemaakt is.
              {brands.some(b => b.name === 'emerginC') && ' emerginC plant bovendien een boom voor elk verkocht product.'}
            </p>
          </div>
          <ul className="grid gap-10 sm:grid-cols-2 lg:col-span-7" data-id="element-6852" data-mp="1ed763221gjfr">
            {brands.map(b => <li key={b.name} className="border-t border-ink/15 pt-6" data-id="element-6853" data-mp="2b0tp9ya3q5b9">
                <h3 className="font-display text-2xl font-light text-ink" data-id="element-6854" data-mp="262s3v6p7dtwm">{b.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-6855" data-mp="21c3atmxvkovd">{b.description}</p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* FAQ; de sectie zelf verdwijnt ook als er geen vragen zijn (geen lege band). */}
      {shopFaq.length > 0 && <section className="bg-sand py-20 sm:py-24 lg:py-32" data-id="element-6856" data-mp="2p263o59m5yv1">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6857" data-mp="2p0mmmcg4vjs7">
            <Faq items={shopFaq} title="Bestellen, ruilen en bijvullen" data-id="element-6858" data-mp="bkbfvzgdh86y" />
          </div>
        </section>}

      <GiftCardBlock data-id="element-6859" data-mp="gj5wqvus4mof" />

      <Drawer open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters" footer={
    // Wissen zo smal als zijn woord, de hoofdknop krijgt de rest: zo past "Toon 43 producten"
    // ook op een telefoon van 320px, zonder afkappen of horizontaal scrollen.
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3" data-id="element-6861" data-mp="zh8qtp26p9t3">
            <button type="button" onClick={f.clear} disabled={!f.hasFilters} className={`${btnSecondary} px-4`} data-id="element-6862" data-mp="zfe548j39dny">
              Wissen
            </button>
            {/* Bij nul resultaten niet "Toon 0 producten": dan is er niets te tonen. */}
            <button type="button" onClick={() => setFiltersOpen(false)} disabled={f.visible.length === 0} className={`${btnPrimary} min-w-0 px-4`} data-id="element-6863" data-mp="1d8vkri8nm68p">
              {f.visible.length === 0 ? 'Geen producten' : `Toon ${plural(f.visible.length, 'product', 'producten')}`}
            </button>
          </div>} data-id="element-6860" data-mp="gkgzqtgehqeh">
        <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} showSkin={false} data-id="element-6864" data-mp="o69lzql8a8qf" />
      </Drawer>
    </>;
}